import type { DetailContext } from './detail-renderers';
import {
  appendHeading,
  appendSubheading,
  createInfoTable,
  addInfoRow,
} from './ui-helpers';
import {
  EXPORT_KIND_NAMES,
  formatFuncType,
  flattenTypes,
} from './wasm-types';
import { analyzeFeatures, FeatureSummary } from './feature-analysis';

export function renderFeatureDetection(context: DetailContext): void {
  const detail = context.detailContainer;
  const importedFuncCount = context.getImportedCount(0);
  const analysis = analyzeFeatures(context.moduleInfo, importedFuncCount);

  appendHeading(detail, 'Features & Target');

  const summaryTable = createInfoTable();
  addInfoRow(summaryTable, 'Spec level', analysis.specLevel);
  addInfoRow(summaryTable, 'Detected features', String(analysis.opcodeFeatureCount));
  addInfoRow(summaryTable, 'Structural features', String(analysis.structuralCount));
  if (analysis.targetFeatureCount > 0) {
    addInfoRow(summaryTable, 'Target features', String(analysis.targetFeatureCount));
  }
  detail.appendChild(summaryTable);

  if (analysis.summaries.length === 0) {
    return;
  }

  appendSubheading(detail, 'Detected Features');
  const grid = document.createElement('div');
  grid.className = 'feature-grid';
  for (const summary of analysis.summaries) {
    grid.appendChild(buildFeatureCard(summary, context, importedFuncCount));
  }
  detail.appendChild(grid);
}

function buildFeatureCard(summary: FeatureSummary, context: DetailContext, importedFuncCount: number): HTMLElement {
  const card = document.createElement('div');
  card.className = 'feature-card';

  const header = document.createElement('div');
  header.className = 'feature-card-header';
  const nameEl = document.createElement('span');
  nameEl.className = 'feature-card-name';
  nameEl.textContent = summary.name;
  header.appendChild(nameEl);
  if (summary.targetStatus) {
    const badge = document.createElement('span');
    badge.className = 'feature-badge feature-badge-' + summary.targetStatus;
    badge.textContent = summary.targetStatus;
    header.appendChild(badge);
  }
  card.appendChild(header);

  if (summary.functionIndices.length > 0) {
    appendFunctionUsage(card, summary.functionIndices, context, importedFuncCount);
  }
  if (summary.opcodes.length > 0) {
    const opcodeList = document.createElement('div');
    opcodeList.className = 'feature-card-opcodes';
    opcodeList.textContent = summary.opcodes.slice(0, 8).join(', ') + (summary.opcodes.length > 8 ? ` (+${summary.opcodes.length - 8} more)` : '');
    card.appendChild(opcodeList);
  }
  const note = summary.structuralDetail ?? summary.fallbackNote;
  if (note) {
    const noteEl = document.createElement('div');
    noteEl.className = 'feature-card-stat';
    noteEl.textContent = note;
    card.appendChild(noteEl);
  }
  return card;
}

// Renders the expandable "N functions" usage list with clickable navigation.
function appendFunctionUsage(card: HTMLElement, functionIndices: number[], context: DetailContext, importedFuncCount: number): void {
  const stat = document.createElement('a');
  stat.className = 'feature-card-stat feature-card-expand';
  stat.href = '#';
  stat.textContent = `${functionIndices.length} function${functionIndices.length > 1 ? 's' : ''}`;

  const funcListContainer = document.createElement('div');
  funcListContainer.className = 'feature-card-funclist';
  funcListContainer.style.display = 'none';

  const maxShow = 15;
  for (let displayIdx = 0; displayIdx < Math.min(functionIndices.length, maxShow); displayIdx++) {
    const globalIdx = functionIndices[displayIdx];
    const localIdx = globalIdx - importedFuncCount;
    const displayName = context.getFunctionName(globalIdx) || `func_${globalIdx}`;
    const funcLink = document.createElement('a');
    funcLink.className = 'callgraph-node';
    funcLink.textContent = displayName;
    funcLink.href = '#';
    funcLink.addEventListener('click', (clickEvent) => {
      clickEvent.preventDefault();
      clickEvent.stopPropagation();
      if (localIdx >= 0) {
        context.navigateToItem('function', localIdx);
      }
    });
    funcListContainer.appendChild(funcLink);
  }
  if (functionIndices.length > maxShow) {
    const moreLabel = document.createElement('span');
    moreLabel.className = 'callgraph-more';
    moreLabel.textContent = `+${functionIndices.length - maxShow} more`;
    funcListContainer.appendChild(moreLabel);
  }

  stat.addEventListener('click', (clickEvent) => {
    clickEvent.preventDefault();
    const isVisible = funcListContainer.style.display !== 'none';
    funcListContainer.style.display = isVisible ? 'none' : 'flex';
  });

  card.appendChild(stat);
  card.appendChild(funcListContainer);
}

export function renderModuleInterface(context: DetailContext): void {
  const detail = context.detailContainer;
  const moduleInfo = context.moduleInfo;
  const flatTypes = flattenTypes(moduleInfo);

  appendHeading(detail, 'Module Interface');

  const summaryTable = createInfoTable();
  addInfoRow(summaryTable, 'Imports', String(moduleInfo.imports.length));
  addInfoRow(summaryTable, 'Exports', String(moduleInfo.exports.length));
  detail.appendChild(summaryTable);

  const columnsContainer = document.createElement('div');
  columnsContainer.className = 'func-detail-columns';
  columnsContainer.style.alignItems = 'start';

  const leftCol = document.createElement('div');
  const rightCol = document.createElement('div');

  const importsHeading = document.createElement('h3');
  importsHeading.className = 'detail-subheading';
  importsHeading.style.padding = '0';
  importsHeading.textContent = `Imports (${moduleInfo.imports.length})`;
  leftCol.appendChild(importsHeading);

  if (moduleInfo.imports.length > 0) {
    const importTable = createInfoTable();
    const sortedImports = moduleInfo.imports.map((entry, index) => ({ entry, index }));
    sortedImports.sort((a, b) => a.entry.fieldName.localeCompare(b.entry.fieldName));
    for (const { entry: importEntry, index: importIndex } of sortedImports) {
      const kindName = EXPORT_KIND_NAMES[importEntry.kind] || 'unknown';
      let signature = '';
      if (importEntry.kind === 0 && importEntry.typeIndex !== undefined && importEntry.typeIndex < flatTypes.length) {
        const typeEntry = flatTypes[importEntry.typeIndex];
        if (typeEntry.kind === 'func') {
          signature = ' ' + formatFuncType(typeEntry);
        }
      }

      const row = document.createElement('div');
      row.className = 'detail-info-row';

      const nameLink = document.createElement('a');
      nameLink.className = 'detail-info-link module-interface-name';
      nameLink.textContent = importEntry.fieldName;
      nameLink.title = `${importEntry.moduleName}.${importEntry.fieldName}`;
      nameLink.href = '#';
      nameLink.addEventListener('click', (event) => {
        event.preventDefault();
        context.navigateToItem('import', importIndex);
      });
      row.appendChild(nameLink);

      if (importEntry.kind === 0 && importEntry.typeIndex !== undefined && signature) {
        const topLevelTypeIdx = context.findTopLevelTypeIndex(importEntry.typeIndex);
        const sigLink = document.createElement('a');
        sigLink.className = 'detail-info-link';
        sigLink.style.flex = '1';
        sigLink.textContent = `${kindName}${signature}`;
        sigLink.href = '#';
        sigLink.addEventListener('click', (event) => {
          event.preventDefault();
          context.navigateToItem('type', topLevelTypeIdx);
        });
        row.appendChild(sigLink);
      } else {
        const valueElement = document.createElement('span');
        valueElement.className = 'detail-info-value';
        valueElement.textContent = `${kindName}${signature}`;
        row.appendChild(valueElement);
      }

      importTable.appendChild(row);
    }
    leftCol.appendChild(importTable);
  }

  const exportsHeading = document.createElement('h3');
  exportsHeading.className = 'detail-subheading';
  exportsHeading.style.padding = '0';
  exportsHeading.textContent = `Exports (${moduleInfo.exports.length})`;
  rightCol.appendChild(exportsHeading);

  if (moduleInfo.exports.length > 0) {
    const exportTable = createInfoTable();
    const sortedExports = moduleInfo.exports.map((entry, index) => ({ entry, index }));
    sortedExports.sort((a, b) => a.entry.name.localeCompare(b.entry.name));
    for (const { entry: exportEntry, index: exportIndex } of sortedExports) {
      const kindName = EXPORT_KIND_NAMES[exportEntry.kind] || 'unknown';
      let signature = '';
      const targetSection = context.getExportTargetSection(exportEntry.kind);
      const targetItemIndex = context.getExportTargetItemIndex(exportEntry.kind, exportEntry.index);

      if (exportEntry.kind === 0) {
        const importedFuncCount = context.getImportedCount(0);
        const localFuncIndex = exportEntry.index - importedFuncCount;
        if (localFuncIndex >= 0 && localFuncIndex < moduleInfo.functions.length) {
          const funcEntry = moduleInfo.functions[localFuncIndex];
          if (funcEntry.typeIndex < flatTypes.length) {
            const typeEntry = flatTypes[funcEntry.typeIndex];
            if (typeEntry.kind === 'func') {
              signature = ' ' + formatFuncType(typeEntry);
            }
          }
        }
      }

      const row = document.createElement('div');
      row.className = 'detail-info-row';

      const navSection = (targetSection && targetItemIndex >= 0) ? targetSection : 'export';
      const navIndex = (targetSection && targetItemIndex >= 0) ? targetItemIndex : exportIndex;

      const nameLink = document.createElement('a');
      nameLink.className = 'detail-info-link module-interface-name';
      nameLink.textContent = exportEntry.name;
      nameLink.title = exportEntry.name;
      nameLink.href = '#';
      nameLink.addEventListener('click', (event) => {
        event.preventDefault();
        context.navigateToItem(navSection, navIndex);
      });
      row.appendChild(nameLink);

      if (exportEntry.kind === 0 && signature) {
        const importedFuncCount = context.getImportedCount(0);
        const localFuncIndex = exportEntry.index - importedFuncCount;
        if (localFuncIndex >= 0 && localFuncIndex < moduleInfo.functions.length) {
          const funcEntry = moduleInfo.functions[localFuncIndex];
          const topLevelTypeIdx = context.findTopLevelTypeIndex(funcEntry.typeIndex);
          const sigLink = document.createElement('a');
          sigLink.className = 'detail-info-link';
          sigLink.style.flex = '1';
          sigLink.textContent = `${kindName}${signature}`;
          sigLink.href = '#';
          sigLink.addEventListener('click', (event) => {
            event.preventDefault();
            context.navigateToItem('type', topLevelTypeIdx);
          });
          row.appendChild(sigLink);
        } else {
          const valueElement = document.createElement('span');
          valueElement.className = 'detail-info-value';
          valueElement.textContent = `${kindName}${signature}`;
          row.appendChild(valueElement);
        }
      } else {
        const valueElement = document.createElement('span');
        valueElement.className = 'detail-info-value';
        valueElement.textContent = `${kindName}${signature}`;
        row.appendChild(valueElement);
      }

      exportTable.appendChild(row);
    }
    rightCol.appendChild(exportTable);
  }

  columnsContainer.appendChild(leftCol);
  columnsContainer.appendChild(rightCol);
  detail.appendChild(columnsContainer);
}
